import React, { useState, useMemo, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CssBaseline,
  Box,
  Divider,
  ListItemButton,
  Chip
} from "@mui/material";
import { Link, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from "@mui/icons-material/Settings";
import ListAltIcon from "@mui/icons-material/ListAlt";
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { jwtDecode } from 'jwt-decode';

import DashboardCharts from "./components/_dashboardChart";
import QuoteManagement from "./components/_quoteManagement";
import SurveyManagementPage from "./components/SurveyManagementPage";
import Settings from "./components/_settings";
import PrivateRoute from "./components/_protectRouter";
import SystemBanner from "./components/SystemBanner";
import { t } from './i18n/useTranslation';

import { logout } from "./services/authService";
import { getCompanyProfile } from "./services/apiService"; 

const drawerWidth = 260;

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); 
  const [lang, setLang] = useState(localStorage.getItem('dealerLanguage') || 'en');
  const [mobileOpen, setMobileOpen] = useState(false); 
  const [companyLogo, setCompanyLogo] = useState(null); 
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const profile = await getCompanyProfile();
        if (profile && profile.logoUrl) {
          setCompanyLogo(profile.logoUrl);
        }
        if (profile && profile.language) {
          localStorage.setItem('dealerLanguage', profile.language);
          setLang(profile.language);
        }
      } catch (error) {
        console.warn("Could not fetch company logo", error);
      }
    };
    fetchLogo();
  }, []);

  const userRoles = useMemo(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return [];

    try {
      const decoded = jwtDecode(token);
      const roleClaimType = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";
      const rawRole = decoded.role || decoded[roleClaimType];
      
      if (Array.isArray(rawRole)) return rawRole;
      if (typeof rawRole === 'string') return [rawRole];
      return [];
    } catch (error) {
      return [];
    }
  }, []); 

  const hasRole = (role) => userRoles.includes(role);
  const isSuperAdmin = hasRole('SUPERADMIN');
  const isAdmin = hasRole('ADMIN') || isSuperAdmin;

  const allSections = useMemo(() => [
    { title: t('nav.dashboard'), path: "dashboard", icon: <HomeIcon /> },
    { title: t('nav.quoteManagement'), path: "quote-management", icon: <ListAltIcon /> },
    { title: t('nav.settings'), path: "settings", icon: <SettingsIcon />, adminOnly: false },
    { 
      title: t('nav.surveyManagement'), 
      path: "survey-management", 
      icon: <DescriptionIcon />, 
      adminOnly: true 
    },
], 
// eslint-disable-next-line react-hooks/exhaustive-deps
[lang]);

  const visibleSections = useMemo(() => {
    return allSections.filter((section) => {
      // If the section is marked as adminOnly, only return it if the user is an Admin
      if (section.adminOnly) {
        return isAdmin;
      }
      // Otherwise, show it to everyone
      return true; 
    });
  }, [allSections, isAdmin]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleLogout = async () => {
    logout();
    navigate('/login');
  };

  const drawer = (
    <div>
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "center", 
          alignItems: "center",
          py: 1
        }}
      >
        <Box
          component="img"
          src={companyLogo || "/Logo_1.jpg"} 
          alt="Company Logo"
          sx={{
            height: 60, 
            maxWidth: "80%", 
            objectFit: "contain",
            cursor: "pointer",
          }}
        />
        {isMobile && (
          <IconButton onClick={handleDrawerClose} sx={{ position: 'absolute', right: 8 }}>
            <CloseIcon />
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      
      {/* SuperAdmin HQ Button */}
      {isSuperAdmin && (
        <Box sx={{ p: 2 }}>
            <ListItemButton
                component={Link}
                to="/super-admin/dashboard"
                sx={{
                    bgcolor: '#fefce8',
                    border: '1px solid #a8851d',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': { bgcolor: '#fef08a' }
                }}
            >
                <AdminPanelSettingsIcon sx={{ color: '#a8851d', mr: 2 }} />
                <ListItemText 
                    primary="SuperAdmin HQ" 
                    primaryTypographyProps={{ fontWeight: 'bold', color: '#a8851d' }}
                />
            </ListItemButton>
            <Divider />
        </Box>
      )}

      <List>
        {visibleSections.map((section) => {
          const isSelected = location.pathname === `/dashboard/${section.path}`; 

          return (
            <ListItem
              key={section.title}
              disablePadding
            >
              <ListItemButton
                component={Link}
                to={section.path}
                onClick={handleDrawerClose} 
                sx={{
                  backgroundColor: isSelected ? "#000000" : "transparent", 
                  "&:hover": {
                    backgroundColor: isSelected ? "#000000" : "#918829", 
                  },
                  color: isSelected ? "#CFB53B" : "#000000", 
                  fontWeight: isSelected ? "bold" : "normal", 
                  borderRadius: 1, 
                  transition: "background-color 0.3s ease", 
                }}
              >
                <IconButton sx={{ mr: 2, color: "inherit" }}>{section.icon}</IconButton>
                <ListItemText primary={section.title} />
              </ListItemButton>
            </ListItem>
          );
        })}

        <ListItemButton
          key="logout"
          onClick={handleLogout}
          sx={{
            marginTop: 'auto', 
            "&:hover": {
              backgroundColor: "rgba(211, 47, 47, 0.08)",
            },
            color: "error.main",
            borderRadius: 1,
          }}
        >
          <IconButton sx={{ mr: 2, color: "inherit" }}>
            <LogoutIcon />
          </IconButton>
          <ListItemText primary={t('nav.logout')} />
        </ListItemButton>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* AppBar */}
      <AppBar
       position="fixed"
       sx={{
         width: isMobile ? "100%" : `calc(100% - ${drawerWidth}px)`,
         ml: isMobile ? 0 : `${drawerWidth}px`,
         bgcolor: "#808080", 
         color: "#FFFFFF", 
       }}
      >
        <Toolbar>
          {isMobile && ( 
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ fontWeight: "bold", flexGrow: 1 }}>
              {t('nav.dashboard')}
          </Typography>
          
          {isSuperAdmin && (
              <Chip 
                label="SUPERADMIN VIEW" 
                size="small" 
                sx={{ bgcolor: '#a8851d', color: 'white', fontWeight: 'bold' }} 
              />
          )}
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      {isMobile ? ( 
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true, 
          }}
          sx={{
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      ) : ( 
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "#f9f9f9",
          p: 3,
          minHeight: "100vh",
          width: isMobile ? "100%" : `calc(100% - ${drawerWidth}px)`,
        }}
      >
        <Toolbar />

        <SystemBanner />
        
        <Routes>
          <Route path="dashboard" element={<DashboardCharts lang={lang} />} />
          <Route path="quote-management" element={<QuoteManagement />} />
          <Route 
            path="survey-management" 
            element={
              <PrivateRoute requiredRole="ADMIN">
                <SurveyManagementPage />
              </PrivateRoute>
            } 
          /> 
          <Route 
            path="settings" 
            element={<Settings lang={lang} />} 
          />
          
          <Route path="*" element={<Navigate to="dashboard" />} /> 
        </Routes>
      </Box>
    </Box>
  );
};

export default Dashboard;